import React, { useState } from 'react'
import { Card, Collapse, Spinner } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { IntegrationOperationFilter, OperationDirection, useRunOperationMutation } from '../../../../../store/integrations/integrations'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { TestResultDisplay } from '../TestResultDisplay'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { IntegrationSettings } from '../../AdminSettings/schema'
import { useFormContext } from 'react-hook-form'
import '../MappingTester.scss'

type SyncResultWithContacts = {
  data?: {
    contact_sync?: {
      data?: unknown[]
    }
  }
}

const hasContactData = (value: unknown): value is SyncResultWithContacts => typeof value === 'object' && value !== null

export interface ContactSyncMappingTesterProps {
  serviceId: IntegrationServiceId
}

export const ContactSyncMappingTester: React.FC<ContactSyncMappingTesterProps> = ({ serviceId }) => {
  const [isOpen, setIsOpen] = useState(false)
  const {
    formState: { isDirty, isValid },
    watch,
  } = useFormContext<IntegrationSettings>()

  const isFormUnsaved = isDirty || !isValid
  const integrationName = getIntegrationDisplayName(serviceId)
  const contactType = watch('mappings.contact.objectName')
  const accountId = useAppSelector(selectAccountId)
  const [sync, { isLoading: syncing, data: syncResult, isSuccess: syncSuccess, isError: syncIsError, error: syncError }] = useRunOperationMutation()

  const handleSyncTest = async () => {
    setIsOpen(true)
    const testSyncMappingFilter: IntegrationOperationFilter = { object: `${contactType}_sync`, platform: serviceId, direction: OperationDirection.INBOUND }
    await sync({ accountId, connector: serviceId, context: { max_object_count: 1000 }, operation: testSyncMappingFilter }).unwrap()
  }

  const syncedContacts: unknown[] =
    hasContactData(syncResult) && Array.isArray(syncResult.data?.contact_sync?.data) ? (syncResult.data?.contact_sync?.data ?? []) : []
  const hasNoContactsWithEmail =
    syncSuccess && !syncIsError && syncedContacts.length > 0 && (syncedContacts as Array<{ email?: string }>).every((c) => !c?.email?.trim())

  const isSuccess = syncSuccess && !syncIsError && !hasNoContactsWithEmail
  const isError = syncIsError || hasNoContactsWithEmail
  const errorMessage = hasNoContactsWithEmail
    ? `None of the synchronized contacts have an email address. Please verify that the email address field is properly configured.`
    : `An unexpected error occurred during the ${integrationName} synchronization process. Please verify your integration settings and field mappings.`

  return (
    <Card className='zocks-border-0.75 border-1 shadow-sm border-dark'>
      <Card.Body className='p-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='bi bi-arrow-repeat fs-5'></i>
              <h6 className='fw-bold mb-0'>Contact Sync</h6>
            </div>
            <p className='text-secondary mb-0 small'>Confirm contact records synchronize from your {integrationName} as configured.</p>
          </div>
          <Tooltip id='contact-sync-run-test-tooltip' title={isFormUnsaved ? 'Save your changes before running a test' : undefined}>
            <Button
              type='button'
              variant='primaryGhost'
              className='d-flex align-items-center gap-2 fw-semibold flex-shrink-0'
              onClick={handleSyncTest}
              disabled={syncing || isFormUnsaved}
            >
              {syncing ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-play-fill' />}
              Run Test
            </Button>
          </Tooltip>
        </div>

        <Collapse in={isOpen}>
          <div>
            <TestResultDisplay
              isLoading={syncing}
              hasResult={syncSuccess || syncIsError}
              isSuccess={isSuccess}
              isError={isError}
              integrationName={integrationName}
              rawResult={syncIsError ? syncError : syncResult}
              successContent={<p className='mb-0 text-dark ps-4'>Enable Contact Sync to start synchronizing them into Zocks.</p>}
              errorContent={<p className='mb-0 text-dark ps-4'>{errorMessage}</p>}
            />
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  )
}
